import React, { useState } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore'; 
import { auth, db } from '../../firebase/config'; 
import { useNavigate } from 'react-router-dom';

export default function Auth() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [location, setLocation] = useState(''); 
  const [isRegistering, setIsRegistering] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();

  const handleAuth = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    try {
      if (isRegistering) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        await setDoc(doc(db, "users", user.uid), {
          email: user.email,
          uid: user.uid,
          role: 'user',
          joinedAt: serverTimestamp(),
          location: location 
        });

      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          await setDoc(userRef, {
            email: user.email,
            uid: user.uid,
            role: 'user', 
            joinedAt: serverTimestamp(),
            location: 'Elkhart, IN', 
            note: 'Backfilled legacy account'
          });
        }
      }
      navigate('/');
    } catch (err) { 
      alert(err.message); 
      setIsProcessing(false);
    }
  };

  // 🚨 NEW: Google Authentication Handler
  const handleGoogleAuth = async () => {
    setIsProcessing(true);
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      // If they don't exist in the DB yet, treat it as a fresh registration
      if (!userSnap.exists()) {
        await setDoc(userRef, {
          email: user.email,
          uid: user.uid,
          role: 'user',
          joinedAt: serverTimestamp(),
          location: 'Elkhart, IN', // Smart baseline fallback
          note: 'Registered via Google OAuth'
        });
      }
      
      navigate('/');
    } catch (err) {
      alert(err.message);
      setIsProcessing(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      
      <div style={{ width: '100%', maxWidth: '440px', backgroundColor: 'white', borderRadius: '24px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1), 0 10px 15px -3px rgba(0, 0, 0, 0.05)', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
        
        {/* Elegant Header */}
        <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', padding: '40px 30px', color: 'white', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px', lineHeight: '1' }}>🛡️</div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '800', letterSpacing: '-0.5px' }}>
            {isRegistering ? 'Join Nundinaria' : 'Welcome Back'}
          </h2>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: '15px' }}>
            {isRegistering ? 'Create an account to start trading.' : 'Securely log in to your dashboard.'}
          </p>
        </div>

        <form onSubmit={handleAuth} style={{ padding: '40px 30px 20px 30px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          <div>
            <label style={labelStyle}>Email Address</label>
            <input 
              type="email" 
              placeholder="you@example.com" 
              onChange={(e) => setEmail(e.target.value)} 
              required 
              style={inputStyle} 
              onFocus={handleFocus} 
              onBlur={handleBlur} 
            />
          </div>

          <div>
            <label style={labelStyle}>Password</label>
            <input 
              type="password" 
              placeholder="••••••••" 
              onChange={(e) => setPassword(e.target.value)} 
              required 
              style={inputStyle} 
              onFocus={handleFocus} 
              onBlur={handleBlur} 
            />
          </div>
          
          {isRegistering && (
            <div style={{ animation: 'fadeIn 0.3s ease' }}>
              <label style={labelStyle}>Home Location</label>
              <input 
                type="text" 
                placeholder="City, State (e.g. Elkhart, IN)" 
                onChange={(e) => setLocation(e.target.value)} 
                required 
                style={inputStyle} 
                onFocus={handleFocus} 
                onBlur={handleBlur} 
              />
              <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#64748b' }}>Used to show you local gear nearby.</p>
            </div>
          )}

          <button 
            type="submit" 
            disabled={isProcessing}
            style={{ 
              marginTop: '10px',
              padding: '16px', 
              backgroundColor: isProcessing ? '#94a3b8' : '#4f46e5', 
              color: 'white', 
              border: 'none', 
              borderRadius: '12px', 
              cursor: isProcessing ? 'not-allowed' : 'pointer', 
              fontWeight: '800', 
              fontSize: '16px', 
              boxShadow: isProcessing ? 'none' : '0 4px 6px -1px rgba(79, 70, 229, 0.3)', 
              transition: 'all 0.2s ease'
            }}
            onMouseOver={e => !isProcessing && (e.currentTarget.style.transform = 'translateY(-2px)')}
            onMouseOut={e => !isProcessing && (e.currentTarget.style.transform = 'translateY(0)')}
          >
            {isProcessing ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                <div style={{ width: '16px', height: '16px', border: '3px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                Authenticating...
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </span>
            ) : (isRegistering ? 'Create Secure Account' : 'Sign In')}
          </button>
        </form>

        {/* 🚨 NEW: Elegant Divider & Google Sign-In Action Block */}
        <div style={{ padding: '0 30px 20px 30px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: '#e2e8f0' }}></div>
            <span style={{ padding: '0 12px', color: '#94a3b8', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Or Context Link</span>
            <div style={{ flex: 1, height: '1px', backgroundColor: '#e2e8f0' }}></div>
          </div>

          <button 
            type="button"
            onClick={handleGoogleAuth}
            disabled={isProcessing}
            style={{ 
              width: '100%', 
              padding: '14px', 
              backgroundColor: 'white', 
              color: '#0f172a', 
              border: '1px solid #cbd5e1', 
              borderRadius: '12px', 
              fontWeight: '700', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '12px', 
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={e => !isProcessing && (e.currentTarget.style.backgroundColor = '#f8fafc')}
            onMouseOut={e => !isProcessing && (e.currentTarget.style.backgroundColor = 'white')}
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" style={{ width: '18px', height: '18px' }} />
            Continue with Google
          </button>
        </div>

        <div style={{ backgroundColor: '#f1f5f9', padding: '20px', textAlign: 'center', borderTop: '1px solid #e2e8f0' }}>
          <p style={{ margin: 0, fontSize: '14px', color: '#475569', fontWeight: '500' }}>
            {isRegistering ? 'Already have an account?' : "Don't have an account?"}
            <span 
              onClick={() => { setIsRegistering(!isRegistering); setIsProcessing(false); }}
              style={{ color: '#4f46e5', fontWeight: '700', marginLeft: '6px', cursor: 'pointer' }}
              onMouseOver={e => e.currentTarget.style.textDecoration = 'underline'}
              onMouseOut={e => e.currentTarget.style.textDecoration = 'none'}
            >
              {isRegistering ? 'Log in here' : 'Sign up now'}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

// Reusable Styles
const labelStyle = { 
  display: 'block', 
  marginBottom: '8px', 
  fontWeight: '700', 
  fontSize: '13px', 
  color: '#0f172a',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
};

const inputStyle = { 
  width: '100%', 
  padding: '14px 16px', 
  borderRadius: '12px', 
  border: '1px solid #cbd5e1', 
  backgroundColor: '#f8fafc', 
  fontSize: '15px', 
  color: '#0f172a', 
  boxSizing: 'border-box',
  outline: 'none',
  transition: 'all 0.2s ease'
};

// Smooth focus handlers
const handleFocus = (e) => {
  e.target.style.backgroundColor = 'white';
  e.target.style.borderColor = '#4f46e5';
  e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
};

const handleBlur = (e) => {
  e.target.style.backgroundColor = '#f8fafc';
  e.target.style.borderColor = '#cbd5e1';
  e.target.style.boxShadow = 'none';
};